import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import {
  cancelObservation,
  getObservations,
  updateObservation,
} from "../../services/observationService";
import PeriodPicker, { lessonFields } from "../../components/scheduling/PeriodPicker";
import useDialog from "../../utils/hooks/useDialog";
import Skeleton from "../../components/Skeleton/Skeleton";
import { fullName } from "../../utils/helpers/userDisplay";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { slotErrorMessage } from "../../utils/helpers/slotErrors";
import {
  daysLate,
  formatMonthShort,
  notBeforeToday,
  parseLocalISODate,
  toLocalISODate,
} from "../../utils/helpers/dateDisplay";
import { OBSERVATION_STATUS } from "../../utils/constants/observations";
import {
  CARD,
  DANGER_QUIET_BTN,
  FOCUS_RING,
  META_LABEL,
  QUIET_BTN,
} from "./coverageStyles";

// Four rows is what fits above the fold beside the tiles; the rest are one
// click away rather than a scroll that pushes the whole table off screen.
const PREVIEW_ROWS = 4;
const QUEUE_PAGE_SIZE = 50;

// "scheduled_date < today", spelled the only way the observations filterset can
// say it: its `scheduled_date` lookups are [exact, gte, lte] with no `lt`, so
// the boundary is yesterday inclusive. For a DateField the two are the same
// set — but the day has to be subtracted here, and silently passing today to
// `__lte` would count every visit still due this morning as overdue.
const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalISODate(d);
};

const daysOverdue = (scheduledDate) => Math.max(0, daysLate(scheduledDate));

function DateBadge({ date }) {
  const parsed = parseLocalISODate(date);
  return (
    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
      <span className="text-base font-black leading-none tabular-nums">
        {parsed ? parsed.getDate() : "—"}
      </span>
      <span className="mt-0.5 text-[9px] font-bold uppercase tracking-wider">
        {parsed ? formatMonthShort(parsed) : ""}
      </span>
    </div>
  );
}

// Reschedule and cancel in one dialog: they are the same decision taken two
// ways ("this visit is not happening as booked"), and two near-identical
// panels would be two places to keep the focus trap and the busy guard right.
function QueueActionDialog({ mode, observation, onClose, onDone }) {
  const { t } = useTranslation();
  const dialogRef = useDialog(onClose);
  const todayISO = toLocalISODate(new Date());
  // Today, never the day that has already gone. Every row in this queue is
  // overdue by definition, so the visit's own date is always in the past here.
  const [date, setDate] = useState(notBeforeToday(observation.scheduled_date));
  const [period, setPeriod] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isCancel = mode === "cancel";
  const canSubmit = isCancel ? true : Boolean(date && period);

  const submit = async () => {
    if (busy || !canSubmit) return;
    setBusy(true);
    try {
      if (isCancel) {
        await cancelObservation({ uuid: observation.uuid, body: { reason } });
        toast.success(t("upcoming.visitCancelledToast"));
      } else {
        await updateObservation({
          uuid: observation.uuid,
          body: {
            scheduled_date: date,
            period,
            ...lessonFields(lesson),
          },
        });
        toast.success(t("upcoming.visitRescheduledToast"));
      }
      onDone();
    } catch (err) {
      toast.error(
        isCancel
          ? formatApiError(err, t("upcoming.cancelFailed"))
          : slotErrorMessage(err, t, t("upcoming.rescheduleFailed")),
      );
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-action-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl outline-none"
      >
        <div className="shrink-0 border-b border-slate-100 bg-slate-50/60 px-6 py-5">
          <h3 id="queue-action-title" className="text-lg font-black text-slate-900">
            {isCancel ? t("upcoming.confirmCancelTitle") : t("upcoming.rescheduleTitle")}
          </h3>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            {t("coverage.queue.dialogSub", {
              teacher: fullName(observation.observed_teacher),
            })}
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {isCancel ? (
            <>
              <p className="text-sm font-semibold text-slate-600">
                {t("upcoming.confirmCancelBody")}
              </p>
              <label className="block space-y-2">
                <span className={`ml-1 block ${META_LABEL}`}>{t("upcoming.cancelReasonLabel")}</span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder={t("upcoming.cancelReasonPlaceholder")}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </label>
            </>
          ) : (
            <>
              <label className="block space-y-2">
                <span className={`ml-1 block ${META_LABEL}`}>{t("upcoming.visitDate")}</span>
                <input
                  type="date"
                  value={date}
                  min={todayISO}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setPeriod(null);
                    setLesson(null);
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </label>
              <div className="space-y-2">
                <span className={`ml-1 block ${META_LABEL}`}>{t("upcoming.pickLessonLabel")}</span>
                <PeriodPicker
                  teacherUuid={observation.observed_teacher?.uuid}
                  date={date}
                  value={period}
                  observerUuid={observation.main_observer?.uuid}
                  observationUuid={observation.uuid}
                  onChange={(p, matched) => {
                    setPeriod(p);
                    setLesson(matched);
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex shrink-0 gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={`flex-1 rounded-2xl bg-slate-100 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition hover:bg-slate-200 disabled:opacity-60 ${FOCUS_RING}`}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !canSubmit}
            className={`flex-1 rounded-2xl py-3 text-xs font-black uppercase tracking-widest text-white transition disabled:opacity-50 ${
              isCancel ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"
            } ${FOCUS_RING}`}
          >
            {isCancel ? t("upcoming.cancelVisitBtn") : t("upcoming.reschedule")}
          </button>
        </div>
      </div>
    </div>
  );
}

function QueueRow({ observation, canAct, mineUuid, onAction }) {
  const { t } = useTranslation();
  const late = daysOverdue(observation.scheduled_date);
  const observer = observation.main_observer;
  // Conducting is offered ONLY on a visit this person is themselves down to
  // run. The server would allow more — `can_manage` also admits anyone holding
  // observations.observations.manage in the school, which every admin reading
  // this queue does — but `submit_observation` never rewrites
  // ObservationObserver, so a visit conducted by an admin on somebody else's
  // booking is still filed under the BOOKED observer's name. That puts an
  // evaluation in the record over a person who was not in the room, and it
  // credits the visit to them on the very page this queue sits on. The queue is
  // a chase list; its verbs for another person's debt are reschedule and
  // cancel, and the name of who owes it is already the first thing in the row.
  const mine =
    Boolean(mineUuid) &&
    (observer?.uuid === mineUuid || observation.created_by?.uuid === mineUuid);
  const context = [observation.group?.name, observation.domain_subject?.name]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4">
      <DateBadge date={observation.scheduled_date} />

      <div className="min-w-[12rem] flex-1">
        <p className="truncate text-sm font-bold text-slate-900">
          {/* Who owes the visit comes first — the queue is a list of debts, and
              the debtor is the main observer, never whoever booked it. */}
          {observer ? fullName(observer) : t("coverage.queue.noObserver")}
          <span className="mx-1.5 text-slate-300" aria-hidden="true">
            →
          </span>
          {fullName(observation.observed_teacher)}
        </p>
        <p className="mt-0.5 truncate text-xs font-medium text-slate-500">
          {[context, observation.period ? t("upcoming.period", { period: observation.period }) : ""]
            .filter(Boolean)
            .join(" · ") || t("myObservations.lessonFallback")}
        </p>
      </div>

      <span className="shrink-0 rounded-lg bg-amber-50 px-2.5 py-1 text-[11px] font-bold leading-none text-amber-700 tabular-nums">
        {t("coverage.queue.daysOverdue", { count: late })}
      </span>

      {canAct && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {mine && (
            <Link
              to={`/observation?visit=${observation.uuid}`}
              className={`inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-indigo-700 ${FOCUS_RING}`}
            >
              {t("coverage.queue.conduct")}
            </Link>
          )}
          <button type="button" className={QUIET_BTN} onClick={() => onAction("reschedule", observation)}>
            {t("upcoming.reschedule")}
          </button>
          <button
            type="button"
            className={DANGER_QUIET_BTN}
            onClick={() => onAction("cancel", observation)}
          >
            {t("coverage.queue.cancel")}
          </button>
        </div>
      )}
    </li>
  );
}

// The overdue queue: every visit that was booked, came due and was never run.
// It is fed by the plain observations list — status=scheduled with a past
// date — so it needs nothing from the coverage endpoint and stays truthful
// while the table beside it is still loading a different period.
export default function OverdueQueue({
  organizationUuid,
  period,
  canAct,
  onChanged,
}) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("loading");
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState(null);
  const sectionRef = useRef(null);
  const headingRef = useRef(null);

  // The same window the tiles count in, so the number above this list and the
  // list itself can never name different sets. The upper bound is whichever
  // comes first: yesterday, or the period's own last day — a period that has
  // not finished is capped by today, one already over by its own end.
  const from = period?.start || null;
  const to = (() => {
    const cutoff = yesterdayISO();
    if (!period?.end) return cutoff;
    return period.end < cutoff ? period.end : cutoff;
  })();
  // A period still in the future cannot hold an overdue booking, and asking for
  // an inverted range would return the whole table, not nothing.
  const inRange = !from || from <= to;

  const load = useCallback(
    (signal) => {
      if (!inRange) {
        setRows([]);
        setTotal(0);
        setStatus("ready");
        return Promise.resolve();
      }
      setStatus("loading");
      return getObservations({
        params: {
          organization: organizationUuid,
          status: OBSERVATION_STATUS.SCHEDULED,
          scheduled_date__gte: from || undefined,
          scheduled_date__lte: to,
          ordering: "scheduled_date",
          page_size: QUEUE_PAGE_SIZE,
        },
        signal,
      })
        .then((data) => {
          if (signal?.aborted) return;
          setRows(data?.results || []);
          setTotal(data?.count ?? 0);
          setStatus("ready");
        })
        .catch((err) => {
          if (signal?.aborted || err?.code === "ERR_CANCELED") return;
          setStatus("error");
        });
    },
    [organizationUuid, from, to, inRange],
  );

  useEffect(() => {
    if (!organizationUuid) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [organizationUuid, load]);

  const visible = showAll ? rows : rows.slice(0, PREVIEW_ROWS);
  const hidden = Math.max(0, rows.length - visible.length);

  return (
    <section ref={sectionRef} className={`${CARD} overflow-hidden`} aria-labelledby="overdue-queue-title">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <button
          type="button"
          ref={headingRef}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="overdue-queue-body"
          className={`-m-1 flex min-w-0 items-start gap-3 rounded-xl p-1 text-left ${FOCUS_RING}`}
        >
          <svg
            className={`mt-1 h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
          {/* Heading and count only. The rows carry the date, the days-overdue
              chip and the actions, so a line of prose under the title was
              explaining what the list below it already shows. */}
          <span className="min-w-0">
            <span id="overdue-queue-title" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
              {t("coverage.queue.title")}
              <span className="rounded-lg bg-rose-50 px-2 py-0.5 text-xs font-black text-rose-600 tabular-nums">
                {status === "ready" ? total : "—"}
              </span>
            </span>
          </span>
        </button>
      </div>

      {open && (
        <div id="overdue-queue-body">
          {status === "loading" ? (
            <div className="space-y-3 px-5 py-4">
              {Array.from({ length: PREVIEW_ROWS }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-2xl" />
              ))}
            </div>
          ) : status === "error" ? (
            <p className="px-5 py-10 text-center text-sm font-semibold text-slate-400">
              {t("dashboard.panelError")}
            </p>
          ) : rows.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-black text-emerald-700">{t("coverage.queue.emptyTitle")}</p>
              <p className="mx-auto mt-1 max-w-sm text-xs font-medium text-slate-500">
                {t("coverage.queue.emptyBody")}
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-slate-100">
                {visible.map((observation) => (
                  <QueueRow
                    key={observation.uuid}
                    observation={observation}
                    canAct={canAct}
                    mineUuid={user?.uuid}
                    onAction={(mode, obs) => setDialog({ mode, observation: obs })}
                  />
                ))}
              </ul>
              {(hidden > 0 || showAll) && (
                <div className="border-t border-slate-100 px-5 py-3">
                  <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className={`rounded-md px-1 text-sm font-bold text-indigo-600 transition hover:text-indigo-700 ${FOCUS_RING}`}
                  >
                    {showAll
                      ? t("coverage.queue.showFewer")
                      : t("coverage.queue.showMore", { count: hidden })}
                  </button>
                </div>
              )}
              {/* The list is capped, so an admin looking at 94 debts must not
                  read the 50 shown as all of them. */}
              {total > rows.length && (
                <p className="border-t border-slate-100 px-5 py-3 text-xs font-semibold text-slate-500">
                  {t("coverage.queue.capped", { shown: rows.length, total })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {dialog && (
        <QueueActionDialog
          mode={dialog.mode}
          observation={dialog.observation}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            load();
            onChanged?.();
          }}
        />
      )}
    </section>
  );
}
